import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Search, Radio, Upload, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";

const DEPTS = [
  { code: "75", name: "Paris" },
  { code: "92", name: "Hauts-de-Seine" },
  { code: "93", name: "Seine-Saint-Denis" },
  { code: "94", name: "Val-de-Marne" },
  { code: "78", name: "Yvelines" },
  { code: "91", name: "Essonne" },
  { code: "95", name: "Val-d'Oise" },
  { code: "77", name: "Seine-et-Marne" },
];

const SECTORS = [
  { key: "commerces", label: "Commerces de proximité" },
  { key: "pme", label: "PME / bureaux" },
  { key: "medical", label: "Santé (cabinets, pharmacies)" },
  { key: "notaires", label: "Notaires / avocats" },
  { key: "ecoles", label: "Écoles / formation / crèches" },
  { key: "restauration", label: "Restauration" },
];

export default function Prospection() {
  const nav = useNavigate();
  const [dept, setDept] = useState("75");
  const [sector, setSector] = useState("commerces");
  const [limit, setLimit] = useState(10);
  const [city, setCity] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState(null);

  // manual form
  const [manual, setManual] = useState({ company: "", contact_name: "", phone: "", email: "", city: "", sector: "" });
  const [manualLoading, setManualLoading] = useState(false);

  const scrape = async (nextPage = 1) => {
    setLoading(true);
    setProgress(0);
    if (nextPage === 1) { setResults([]); setMeta(null); }
    const timer = setInterval(() => setProgress((p) => Math.min(p + Math.random() * 12, 92)), 250);
    try {
      const r = await api.post("/prospects/scrape", { department: dept, sector, city, limit: Number(limit), page: nextPage });
      setResults((prev) => nextPage === 1 ? r.data.prospects : [...prev, ...r.data.prospects]);
      setMeta(r.data);
      setPage(r.data.next_page);
      setProgress(100);
      if (r.data.count === 0) toast.info(r.data.skipped_duplicates > 0 ? "Toutes ces entreprises sont déjà dans votre pipeline — essayez « Chercher plus »" : "Aucune entreprise trouvée pour ces critères");
      else toast.success(`${r.data.count} prospects réels ajoutés au pipeline`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de recherche");
    } finally {
      clearInterval(timer);
      setLoading(false);
    }
  };

  const onCriteriaChange = (setter) => (v) => { setter(v); setPage(1); };

  const submitManual = async (e) => {
    e.preventDefault();
    setManualLoading(true);
    try {
      await api.post("/prospects", manual);
      toast.success("Prospect créé");
      setManual({ company: "", contact_name: "", phone: "", email: "", city: "", sector: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setManualLoading(false);
    }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/prospects/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.count} prospects importés`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur import");
    }
    e.target.value = "";
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <header className="mb-8">
        <div className="text-xs font-mono uppercase tracking-widest text-cyan-400/80 mb-2">// Prospection</div>
        <h1 className="font-heading text-4xl font-extrabold text-slate-100 tracking-tight">Trouver des prospects</h1>
        <p className="text-slate-400 mt-1.5 text-sm">Recherche automatique par département et secteur, ou import CSV / création manuelle.</p>
      </header>

      {/* Scraping panel */}
      <div className="p-6 bg-[#121215] border border-slate-800/80 rounded-xl mb-6 fade-up">
        <div className="flex items-center gap-2 mb-5">
          <Radio className="w-4 h-4 text-cyan-400" />
          <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Recherche réelle — Île-de-France</div>
          <span className="ml-auto text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">Base SIRENE officielle + OpenStreetMap</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Département</label>
            <select
              data-testid="scraping-department-select"
              value={dept}
              onChange={(e) => onCriteriaChange(setDept)(e.target.value)}
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 focus:border-cyan-500/60 outline-none"
            >
              {DEPTS.map((d) => <option key={d.code} value={d.code}>{d.code} — {d.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Ville (optionnel)</label>
            <input
              data-testid="scraping-city-input"
              value={city}
              onChange={(e) => onCriteriaChange(setCity)(e.target.value)}
              placeholder="ex. Morsang-sur-Orge"
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 focus:border-cyan-500/60 outline-none text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Secteur</label>
            <select
              data-testid="scraping-sector-select"
              value={sector}
              onChange={(e) => onCriteriaChange(setSector)(e.target.value)}
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 focus:border-cyan-500/60 outline-none"
            >
              {SECTORS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Nombre de leads</label>
            <input
              data-testid="scraping-limit-input"
              type="number"
              min="1"
              max="25"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              className="mt-1.5 w-full px-3 py-2.5 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 focus:border-cyan-500/60 outline-none"
            />
          </div>
          <div className="flex items-end">
            <button
              data-testid="scraping-launch-button"
              onClick={() => scrape(1)}
              disabled={loading}
              className="w-full py-2.5 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
            >
              <Search className="w-4 h-4" />
              {loading ? "Recherche…" : "Lancer la prospection"}
            </button>
          </div>
        </div>
        <p className="mt-3 text-[11px] text-slate-500">
          Entreprises actives (PME/TPE) issues du registre officiel SIRENE, dirigeant, adresse, activité et effectif. Téléphone / site web complétés via OpenStreetMap quand disponibles — l'email des entreprises n'est pas public : privilégiez le script d'appel. Les particuliers (B2C) s'ajoutent manuellement ou par CSV.
        </p>

        {(loading || progress > 0) && (
          <div className="mt-5">
            <div className="flex items-center justify-between text-xs font-mono text-slate-500 mb-2">
              <span>{progress < 60 ? "Interrogation du registre SIRENE…" : progress < 100 ? "Enrichissement OpenStreetMap…" : "Terminé"}</span>
              <span data-testid="scraping-progress">{Math.round(progress)}%</span>
            </div>
            <div className="h-1.5 bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-cyan-500 to-emerald-400 transition-all duration-200" style={{ width: `${progress}%` }} />
            </div>
          </div>
        )}
      </div>

      {meta && (
        <div className="mb-6 p-6 bg-[#121215] border border-emerald-500/30 rounded-xl fade-up">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div>
              <div data-testid="scraping-summary" className="text-sm font-semibold text-emerald-400">
                {results.length} prospects réels ajoutés au pipeline
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                {meta.sector_label}{meta.city_label ? ` · ${meta.city_label}` : ` · département ${dept}`} · {meta.total_available.toLocaleString("fr-FR")} entreprises disponibles
                {meta.skipped_duplicates > 0 && ` · ${meta.skipped_duplicates} déjà dans le pipeline (ignorées)`}
                {meta.enriched > 0 && ` · ${meta.enriched} avec téléphone/site (OSM)`}
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                data-testid="scraping-more-button"
                onClick={() => scrape(page)}
                disabled={loading}
                className="px-3 py-1.5 rounded-lg text-xs bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-100 disabled:opacity-60"
              >
                Chercher plus (page {page})
              </button>
              <button data-testid="goto-pipeline-button" onClick={() => nav("/prospects")} className="text-xs text-cyan-400 hover:text-cyan-300">
                Voir le pipeline →
              </button>
            </div>
          </div>
          <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
            {results.map((p) => (
              <button key={p.prospect_id} data-testid={`result-row-${p.prospect_id}`} onClick={() => nav(`/prospects/${p.prospect_id}`)} className="w-full text-left flex items-center justify-between px-3 py-2 bg-slate-900/60 rounded-lg border border-slate-800 hover:border-cyan-500/40 transition-colors">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-200 truncate">{p.company}{p.naf_label && <span className="ml-2 text-[10px] font-mono uppercase text-cyan-300/80">{p.naf_label}</span>}</div>
                  <div className="text-xs text-slate-500 truncate">
                    {p.contact_name || "Dirigeant non renseigné"} · {p.address ? `${p.address}, ` : ""}{p.postal_code} {p.city}
                    {p.phone && <span className="text-emerald-400"> · {p.phone}</span>}
                    {p.website && <span className="text-cyan-400"> · site web</span>}
                  </div>
                </div>
                <div className="text-right shrink-0 ml-3">
                  <div className="text-xs font-mono text-amber-400">{p.estimated_value}€</div>
                  <div className="text-[10px] font-mono text-slate-600">{p.effectif}</div>
                </div>
              </button>
            ))}
            {results.length === 0 && <div className="text-xs text-slate-500 text-center py-4">Aucun nouveau prospect sur cette page — essayez « Chercher plus ».</div>}
          </div>
        </div>
      )}

      {/* Bottom row: manual + CSV */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <form onSubmit={submitManual} className="p-6 bg-[#121215] border border-slate-800/80 rounded-xl">
          <div className="flex items-center gap-2 mb-5">
            <Plus className="w-4 h-4 text-amber-400" />
            <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Ajout manuel</div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {[
              ["company", "Entreprise *", true],
              ["contact_name", "Contact", false],
              ["phone", "Téléphone", false],
              ["email", "Email", false],
              ["city", "Ville", false],
              ["sector", "Secteur", false],
            ].map(([key, label, req]) => (
              <div key={key} className={key === "company" ? "col-span-2" : ""}>
                <label className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</label>
                <input
                  data-testid={`manual-${key}-input`}
                  required={req}
                  value={manual[key]}
                  onChange={(e) => setManual({ ...manual, [key]: e.target.value })}
                  className="mt-1.5 w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-slate-800 text-slate-100 focus:border-cyan-500/60 outline-none text-sm"
                />
              </div>
            ))}
          </div>
          <button
            data-testid="manual-submit-button"
            disabled={manualLoading}
            className="mt-4 w-full py-2.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-100 font-medium text-sm transition-colors disabled:opacity-60"
          >
            {manualLoading ? "…" : "Ajouter au pipeline"}
          </button>
        </form>

        <div className="p-6 bg-[#121215] border border-slate-800/80 rounded-xl flex flex-col">
          <div className="flex items-center gap-2 mb-5">
            <Upload className="w-4 h-4 text-purple-400" />
            <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Import CSV</div>
          </div>
          <p className="text-sm text-slate-400 mb-4">
            Colonnes reconnues : <span className="font-mono text-slate-300">company, contact_name, phone, email, city, sector, notes</span>
          </p>
          <label className="cursor-pointer flex-1 flex flex-col items-center justify-center border-2 border-dashed border-slate-800 rounded-lg py-10 hover:border-cyan-500/50 transition-colors">
            <Upload className="w-8 h-8 text-slate-600 mb-2" />
            <div className="text-sm text-slate-400">Cliquez pour choisir un fichier .csv</div>
            <input data-testid="import-csv-file-input" type="file" accept=".csv" className="hidden" onChange={uploadCsv} />
          </label>
        </div>
      </div>
    </div>
  );
}
